import classNames from 'classnames'
import React, { ReactNode } from 'react'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Truncate } from '@ui/Truncate/Truncate'
import { Body1, Body2, Caption1 } from '@ui/Typography'
import {
  Color,
  PROGRESS_BAR_DURATION,
  ProgressBar,
  ProgressBarProps,
  Size,
} from '@ui/components/ProgressBar/ProgressBar'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'

import { Box } from '../Box'

import styles from './ProgressBarsBlock.module.scss'

type LabelPosition = 'flex-start' | 'flex-end' | 'center'
type StripesVariant = ProgressBarProps['stripesVariant']

export interface ProgressBarsBlockSection {
  id?: string
  percent: number | null
  isCurrent: boolean
  label?: string
  labelPosition?: LabelPosition
  tooltip?: string
  isNextStarted?: boolean
  stripesVariant?: StripesVariant
}

type ProgressBarsBlockItemExtraInfo =
  | {
      extraInfo?: ReactNode
      sensitiveExtraInfo?: never
    }
  | {
      extraInfo?: never
      sensitiveExtraInfo?: string | null
    }

export type ProgressBarsBlockItem = {
  id?: string
  title: ReactNode
  value?: ReactNode
  tooltip?: string
  progressTooltip?: string
  sections: ProgressBarsBlockSection[]
} & ProgressBarsBlockItemExtraInfo

interface ProgressBarsBlockProps {
  items: ProgressBarsBlockItem[]
  className?: string
  color?: Color
  compact?: boolean
  size?: Size
  useBodyTypography?: boolean
}

interface ProgressRowProps {
  item: ProgressBarsBlockItem
  color: Color
  size: Size
  useBodyTypography: boolean
}

interface ProgressSectionProps extends ProgressBarsBlockSection {
  color: Color
  size: Size
  delay: number
  tooltip?: string
}

export const ProgressBarsBlock = ({
  items,
  className,
  color = 'red',
  compact = false,
  size = 'default',
  useBodyTypography = true,
}: ProgressBarsBlockProps) => {
  return (
    <div className={classNames(styles.root, className, { [styles.compact]: compact })}>
      {items.map((item, index) => (
        <ProgressRow
          key={item.id ?? `progress-row-${index}`}
          item={item}
          color={color}
          size={size}
          useBodyTypography={useBodyTypography}
        />
      ))}
    </div>
  )
}

const ProgressRow = ({ item, color, size, useBodyTypography }: ProgressRowProps) => {
  const { title, value, sections, tooltip, progressTooltip } = item

  const renderedExtraInfo =
    'extraInfo' in item ? (
      item.extraInfo
    ) : item.sensitiveExtraInfo ? (
      <Body1>
        <SensitiveData>{item.sensitiveExtraInfo}</SensitiveData>
      </Body1>
    ) : null

  return (
    <div className={styles.row}>
      <Box flex justify='space-between' align='center' className={styles.head}>
        <div className={styles.title}>
          <Tooltip direction='topRight' disabled={!tooltip} content={tooltip}>
            {useBodyTypography ? <Body1>{title}</Body1> : <>{title}</>}
          </Tooltip>
        </div>

        {value != null && (useBodyTypography ? <Body2>{value}</Body2> : <>{value}</>)}
      </Box>

      <Box flex gap='x8' className={styles.progress}>
        {sections.map((section, index) => (
          <ProgressSection
            key={section.id ?? `progress-section-${index}`}
            {...section}
            color={color}
            size={size}
            tooltip={progressTooltip}
            delay={index * PROGRESS_BAR_DURATION * 0.9}
          />
        ))}
      </Box>

      {renderedExtraInfo != null && (
        <section className={styles.extraInfo}>{renderedExtraInfo}</section>
      )}
    </div>
  )
}

const ProgressSection = ({
  percent,
  label,
  labelPosition = 'center',
  color,
  isCurrent = false,
  isNextStarted,
  tooltip,
  delay,
  size,
  stripesVariant,
}: ProgressSectionProps) => {
  const shouldShowTooltip =
    (isCurrent && percent !== null && percent >= 0) || (!isCurrent && !isNextStarted)

  return (
    <div className={styles.section}>
      <ProgressBar
        color={color}
        percent={percent}
        tooltip={shouldShowTooltip ? tooltip : null}
        delay={delay}
        isCurrent={isCurrent}
        size={size}
        stripesVariant={stripesVariant}
      />

      {label && (
        <Box justify={labelPosition} className={styles.labelWrapper}>
          <Truncate>
            <Caption1 color='text-med-em' className={styles.label}>
              {label}
            </Caption1>
          </Truncate>
        </Box>
      )}
    </div>
  )
}
